import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { getPromptTemplates, getCategories } from '../../api';
import type { PromptTemplate, PromptTemplateCategory } from '../../api';

const CONTENT_PREVIEW_LENGTH = 120;

interface TemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (templateId: string) => Promise<void>;
  excludeIds?: string[];
}

function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen).trim() + '…';
}

export default function TemplatePickerModal({
  open,
  onClose,
  onSelect,
  excludeIds = [],
}: TemplatePickerModalProps) {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [categories, setCategories] = useState<PromptTemplateCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedCategoryId(null);
      setLoading(true);
      Promise.all([getPromptTemplates({ limit: 200 }), getCategories()])
        .then(([tplRes, catRes]) => {
          setTemplates(tplRes.items ?? []);
          setCategories(catRes ?? []);
        })
        .catch(() => {
          setTemplates([]);
          setCategories([]);
        })
        .finally(() => setLoading(false));
    }
  }, [open]);

  const available = useMemo(() => {
    return templates.filter((t) => !excludeIds.includes(t.id));
  }, [templates, excludeIds]);

  const filtered = useMemo(() => {
    let result = available;
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description?.toLowerCase().includes(q) ?? false) ||
          t.content.toLowerCase().includes(q)
      );
    }
    if (selectedCategoryId) {
      result = result.filter((t) => t.categories?.some((c) => c.id === selectedCategoryId));
    }
    return result;
  }, [available, search, selectedCategoryId]);

  const handleSelect = useCallback(
    async (templateId: string) => {
      setSubmitting(templateId);
      try {
        await onSelect(templateId);
      } finally {
        setSubmitting(null);
      }
    },
    [onSelect]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>Добавить промпт в кейс</DialogTitle>
      <DialogContent
        sx={{
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ mb: 2, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Поиск по названию, описанию или тексту..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              {categories.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="Все"
                    onClick={() => setSelectedCategoryId(null)}
                    variant={selectedCategoryId === null ? 'filled' : 'outlined'}
                    size="small"
                  />
                  {categories.map((cat) => (
                    <Chip
                      key={cat.id}
                      label={cat.title}
                      onClick={() =>
                        setSelectedCategoryId(selectedCategoryId === cat.id ? null : cat.id)
                      }
                      variant={selectedCategoryId === cat.id ? 'filled' : 'outlined'}
                      size="small"
                      sx={{
                        borderColor: cat.color,
                        ...(selectedCategoryId === cat.id
                          ? { bgcolor: cat.color, color: '#fff' }
                          : { color: cat.color }),
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {available.length === 0 ? (
              <Box py={3} color="text.secondary" textAlign="center">
                {excludeIds.length > 0
                  ? 'Все подходящие шаблоны уже добавлены в кейс.'
                  : 'Нет доступных шаблонов.'}
              </Box>
            ) : filtered.length === 0 ? (
              <Box py={3} color="text.secondary" textAlign="center">
                Ничего не найдено. Измените поиск или фильтр категории.
              </Box>
            ) : (
              <Box
                sx={{
                  height: 360,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  pb: 4,
                }}
              >
                {filtered.map((t) => (
                  <Card
                    key={t.id}
                    variant="outlined"
                    sx={{
                      flexShrink: 0,
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <CardContent
                      sx={{
                        py: 1.5,
                        '&:last-child': { pb: 1.5 },
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {t.title}
                        </Typography>
                        {t.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {t.description}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            fontSize: '0.8rem',
                            lineHeight: 1.4,
                          }}
                        >
                          {truncate(t.content, CONTENT_PREVIEW_LENGTH)}
                        </Typography>
                        {t.categories?.length ? (
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {t.categories.map((c) => (
                              <Chip
                                key={c.id}
                                label={c.title}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: `${c.color}20`,
                                  borderColor: c.color,
                                  color: c.color,
                                }}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          submitting === t.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <AddIcon fontSize="small" />
                          )
                        }
                        disabled={submitting !== null}
                        onClick={() => handleSelect(t.id)}
                        sx={{ flexShrink: 0 }}
                      >
                        {submitting === t.id ? '' : 'Добавить'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {filtered.length > 0 && `${filtered.length} из ${available.length}`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="contained">
          Готово
        </Button>
      </DialogActions>
    </Dialog>
  );
}
